import { DragEvent, useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import useUploadCreativeInputMutation from "api/hooks/useUploadCreativeInputMutation";
import { extractApiErrorMessage } from "utils/util";
import DSBox from "views/system-design/DSBox";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSIconButton from "views/system-design/DSIconButton";
import DSTypography from "views/system-design/DSTypography";
import {
  MAX_REFERENCE_IMAGES,
  MAX_REFERENCE_IMAGE_BYTES,
  REFERENCE_IMAGE_TYPES,
} from "../utils/creativeOptions";

export type ReferenceImage = {
  key: string;
  name: string;
  previewUrl: string;
  status: "uploading" | "done" | "error";
  url?: string;
  error?: string;
};

type Props = {
  images: ReferenceImage[];
  onChange: (update: (images: ReferenceImage[]) => ReferenceImage[]) => void;
  disabled?: boolean;
};

let nextKey = 0;

/**
 * Optional reference images. Each file is uploaded as soon as it is picked
 * (one request per file), so the job only carries URLs.
 */
const CreativeReferenceImages = ({ images, onChange, disabled }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const { mutateAsync: upload } = useUploadCreativeInputMutation();

  // Free the local previews when the component goes away.
  const imagesRef = useRef(images);
  imagesRef.current = images;
  useEffect(
    () => () =>
      imagesRef.current.forEach((image) =>
        URL.revokeObjectURL(image.previewUrl),
      ),
    [],
  );

  const update = (key: string, patch: Partial<ReferenceImage>) =>
    onChange((current) =>
      current.map((image) =>
        image.key === key ? { ...image, ...patch } : image,
      ),
    );

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    setRejected(null);
    const room = MAX_REFERENCE_IMAGES - images.length;
    const files = Array.from(fileList);
    const problems: string[] = [];

    const accepted = files.filter((file) => {
      if (!REFERENCE_IMAGE_TYPES.includes(file.type)) {
        problems.push(`${file.name} is not a JPEG, PNG or WebP image`);
        return false;
      }
      if (file.size > MAX_REFERENCE_IMAGE_BYTES) {
        problems.push(`${file.name} is larger than 15 MB`);
        return false;
      }
      return true;
    });
    if (accepted.length > room) {
      problems.push(`You can add up to ${MAX_REFERENCE_IMAGES} images`);
    }
    if (problems.length) setRejected(problems.join(". "));

    const added = accepted.slice(0, Math.max(room, 0)).map((file) => ({
      file,
      image: {
        key: `ref-${(nextKey += 1)}`,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
        status: "uploading" as const,
      },
    }));
    if (!added.length) return;

    onChange((current) => [...current, ...added.map(({ image }) => image)]);
    added.forEach(({ file, image }) => {
      upload(file)
        .then((url) => update(image.key, { status: "done", url }))
        .catch((error) =>
          update(image.key, {
            status: "error",
            error: extractApiErrorMessage(error, "Upload failed"),
          }),
        );
    });
  };

  const remove = (image: ReferenceImage) => {
    URL.revokeObjectURL(image.previewUrl);
    onChange((current) => current.filter((item) => item.key !== image.key));
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) addFiles(event.dataTransfer.files);
  };

  const full = images.length >= MAX_REFERENCE_IMAGES;

  return (
    <DSBox>
      <DSBox
        role="button"
        tabIndex={disabled || full ? -1 : 0}
        aria-disabled={disabled || full}
        onClick={() => !disabled && !full && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (
            (event.key === "Enter" || event.key === " ") &&
            !disabled &&
            !full
          ) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        sx={{
          border: "2px dashed",
          borderColor: dragging ? "primary.main" : "divider",
          borderRadius: 2,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          cursor: disabled || full ? "not-allowed" : "pointer",
          backgroundColor: dragging ? "action.selected" : "action.hover",
          opacity: disabled || full ? 0.6 : 1,
          transition: "all 0.2s ease-in-out",
        }}
      >
        <AddPhotoAlternateOutlinedIcon />
        <DSTypography variant="body2">
          {full
            ? `${MAX_REFERENCE_IMAGES} images added`
            : "Drop images here, or click to choose (JPEG, PNG or WebP, up to 15 MB each)"}
        </DSTypography>
        <input
          ref={inputRef}
          type="file"
          accept={REFERENCE_IMAGE_TYPES.join(",")}
          multiple
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </DSBox>

      {rejected && (
        <DSTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {rejected}
        </DSTypography>
      )}

      {images.length > 0 && (
        <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}>
          {images.map((image) => (
            <DSBox
              key={image.key}
              title={image.error ?? image.name}
              sx={{
                position: "relative",
                width: 88,
                height: 88,
                borderRadius: "8px",
                overflow: "hidden",
                border: "2px solid",
                borderColor:
                  image.status === "error" ? "error.main" : "divider",
              }}
            >
              <img
                src={image.previewUrl}
                alt={image.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: image.status === "uploading" ? 0.5 : 1,
                }}
              />
              {image.status === "uploading" && (
                <DSBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DSCircularProgress size={24} />
                </DSBox>
              )}
              {image.status === "error" && (
                <DSTypography
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    fontSize: "10px",
                    textAlign: "center",
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                  }}
                >
                  Upload failed
                </DSTypography>
              )}
              <DSIconButton
                size="small"
                onClick={() => remove(image)}
                disabled={disabled}
                title="Remove"
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  p: 0.25,
                  backgroundColor: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </DSIconButton>
            </DSBox>
          ))}
        </DSBox>
      )}
    </DSBox>
  );
};

export default CreativeReferenceImages;
